import React from "react";

/**
 * Real(istic) line chart: USD share of global FX reserves, 2000-2024.
 * Drawn as a single FT-style line. No 3D, no gradients, no chart-junk.
 * Source attribution shown as caption.
 */

// Annual data points (illustrative, IMF COFER style)
const DATA = [
    { year: 2000, val: 71.9 },
    { year: 2002, val: 67.0 },
    { year: 2004, val: 65.5 },
    { year: 2006, val: 65.0 },
    { year: 2008, val: 64.1 },
    { year: 2010, val: 62.2 },
    { year: 2012, val: 61.5 },
    { year: 2014, val: 65.2 },
    { year: 2016, val: 65.4 },
    { year: 2018, val: 61.8 },
    { year: 2020, val: 58.9 },
    { year: 2022, val: 58.4 },
    { year: 2024, val: 57.8 },
];

const VB_W = 760;
const VB_H = 280;
const PAD = { top: 28, right: 24, bottom: 36, left: 36 };

function buildPath() {
    const xs = DATA.map((d) => d.year);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = 55, maxY = 75; // chart range fixed for editorial clarity
    const innerW = VB_W - PAD.left - PAD.right;
    const innerH = VB_H - PAD.top - PAD.bottom;
    const sx = (x) => PAD.left + ((x - minX) / (maxX - minX)) * innerW;
    const sy = (y) => PAD.top + (1 - (y - minY) / (maxY - minY)) * innerH;
    const points = DATA.map((d) => [sx(d.year), sy(d.val)]);
    const path = points
        .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
        .join(" ");
    return { path, points, sx, sy, minX, maxX, minY, maxY };
}

export default function WhyNow() {
    const { path, points, sx, sy, minY, maxY } = buildPath();
    const last = points[points.length - 1];
    const first = points[0];
    const yTicks = [60, 65, 70, 75];

    return (
        <section
            id="why-now"
            className="bt-section"
            data-testid="section-why-now"
            style={{
                borderTop: "1px solid var(--bt-rule)",
                backgroundColor: "var(--bt-paper)",
            }}
        >
            <div className="bt-shell">
                <div
                    className="bt-promise-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 32,
                        marginBottom: 64,
                    }}
                >
                    <aside style={{ paddingTop: 14 }}>
                        <p className="signal" style={{ marginBottom: 14 }}>
                            № 02
                        </p>
                        <p className="marginalia" style={{ margin: 0, color: "var(--bt-mute)" }}>
                            The dispatch
                        </p>
                    </aside>

                    <div className="bt-read">
                        <h2 className="t-display" style={{ margin: 0, marginBottom: 28 }}>
                            The next ten years won&rsquo;t look like the last forty.
                        </h2>
                        <p className="t-lede" style={{ margin: 0, marginBottom: 20 }}>
                            Most retirement playbooks were written for a world that no
                            longer exists. A world with 5% bond yields, a stable dollar,
                            and a federal balance sheet that wasn&rsquo;t on fire. That
                            world ended somewhere between 2020 and 2024 and most of the
                            financial industry hasn&rsquo;t told you yet.
                        </p>
                        <p className="t-body" style={{ margin: 0 }}>
                            None of this means panic. It means the people who pay
                            attention will end up in a different position than the
                            people who don&rsquo;t.
                        </p>
                    </div>
                </div>

                {/* Chart anchor */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 48,
                        alignItems: "start",
                    }}
                    className="bt-signal-grid"
                >
                    <figure
                        style={{ margin: 0 }}
                        data-testid="reserve-chart-figure"
                    >
                        <figcaption
                            className="marginalia"
                            style={{
                                margin: 0,
                                marginBottom: 12,
                                color: "var(--bt-mute)",
                            }}
                        >
                            Figure 01 &nbsp;·&nbsp; Dollar share of global FX reserves
                        </figcaption>

                        <div
                            style={{
                                borderTop: "1px solid var(--bt-ink)",
                                paddingTop: 8,
                                paddingBottom: 8,
                                borderBottom: "1px solid var(--bt-rule)",
                            }}
                        >
                            <svg
                                viewBox={`0 0 ${VB_W} ${VB_H}`}
                                width="100%"
                                role="img"
                                aria-label="Line chart of US dollar share of global foreign exchange reserves, 2000 to 2024, declining from 71.9 percent to 57.8 percent."
                                style={{ display: "block" }}
                            >
                                {/* Y gridlines (faint) */}
                                {yTicks.map((t) => (
                                    <g key={t}>
                                        <line
                                            x1={PAD.left}
                                            x2={VB_W - PAD.right}
                                            y1={sy(t)}
                                            y2={sy(t)}
                                            stroke="var(--bt-rule)"
                                            strokeWidth="1"
                                            strokeDasharray={t === 60 ? "0" : "2 4"}
                                            opacity={t === 60 ? 0.6 : 0.45}
                                        />
                                        <text
                                            x={PAD.left - 8}
                                            y={sy(t) + 3}
                                            textAnchor="end"
                                            className="bt-chart-axis-label"
                                        >
                                            {t}%
                                        </text>
                                    </g>
                                ))}
                                {/* X axis labels */}
                                {[2000, 2008, 2016, 2024].map((yr) => (
                                    <text
                                        key={yr}
                                        x={sx(yr)}
                                        y={VB_H - PAD.bottom + 18}
                                        textAnchor="middle"
                                        className="bt-chart-axis-label"
                                    >
                                        {yr}
                                    </text>
                                ))}
                                {/* The line */}
                                <path
                                    d={path}
                                    fill="none"
                                    stroke="var(--bt-ink)"
                                    strokeWidth="1.6"
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                />
                                {/* Endpoint markers */}
                                <circle cx={first[0]} cy={first[1]} r="3" fill="var(--bt-ink)" />
                                <circle cx={last[0]} cy={last[1]} r="4" fill="var(--bt-brass)" />
                                {/* Endpoint labels */}
                                <text
                                    x={first[0] + 8}
                                    y={first[1] - 10}
                                    fontFamily="Geist, system-ui, sans-serif"
                                    fontSize="12"
                                    fontWeight="500"
                                    fill="var(--bt-ink)"
                                >
                                    71.9% &nbsp;2000
                                </text>
                                <text
                                    x={last[0] - 6}
                                    y={last[1] + 22}
                                    textAnchor="end"
                                    fontFamily="Geist, system-ui, sans-serif"
                                    fontSize="12"
                                    fontWeight="500"
                                    fill="var(--bt-brass)"
                                >
                                    57.8% &nbsp;2024
                                </text>
                            </svg>
                        </div>

                        <figcaption
                            className="t-meta"
                            style={{
                                marginTop: 10,
                                color: "var(--bt-mute)",
                                fontSize: 11,
                            }}
                        >
                            Source: IMF COFER, year-end allocated reserves. Illustrative.
                        </figcaption>
                    </figure>

                    {/* Three quiet data callouts beside the chart */}
                    <div>
                        <p
                            className="signal"
                            style={{ marginBottom: 18, color: "var(--bt-brass)" }}
                        >
                            The Quiet Watch
                        </p>
                        <ul
                            role="list"
                            style={{
                                listStyle: "none",
                                padding: 0,
                                margin: 0,
                                display: "grid",
                                gap: 24,
                            }}
                            data-testid="why-now-stats"
                        >
                            <Datum
                                value="More than defense"
                                label="Federal interest payments now exceed national defense spending."
                            />
                            <Datum
                                value="Since 1968"
                                label="Foreign central banks are buying gold at the fastest pace in 56 years."
                            />
                            <Datum
                                value="Twice since 2019"
                                label="The rules around 401(k) and IRA withdrawals have changed twice, with more changes pending."
                            />
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Datum({ value, label }) {
    return (
        <li
            style={{
                paddingTop: 18,
                borderTop: "1px solid var(--bt-rule)",
            }}
        >
            <div
                className="t-h3"
                style={{
                    color: "var(--bt-ink)",
                    marginBottom: 8,
                }}
            >
                {value}
            </div>
            <p
                className="t-body"
                style={{ margin: 0, fontSize: 16, color: "var(--bt-ink-soft)" }}
            >
                {label}
            </p>
        </li>
    );
}
